/**
 * PostMessage Bridge
 *
 * Enables bidirectional communication between the casino iframe
 * and the parent/host platform via window.postMessage.
 *
 * OUTGOING EVENTS (casino -> parent):
 *   CASINO_READY            - iframe loaded and user authenticated
 *   CASINO_BET_PLACED       - a bet was successfully placed
 *   CASINO_BET_SETTLED      - a bet result was determined
 *   CASINO_BALANCE_UPDATED  - user balance changed
 *   CASINO_GAME_OPENED      - user navigated to a game
 *   CASINO_GAME_CLOSED      - user left a game (back to lobby)
 *   CASINO_SESSION_EXPIRED  - auth token expired / 401 received
 *   CASINO_EXIT             - user requested to leave the casino
 *   CASINO_ERROR            - an error occurred
 *
 * INCOMING EVENTS (parent -> casino):
 *   FORCE_LOGOUT            - parent requests logout
 *   REFRESH_BALANCE         - parent requests balance refresh
 */

const BRIDGE_PREFIX = "CASINO_IFRAME";

const isInIframe = () => {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
};

// Resolve parent origin dynamically so the same build works for any host.
// Priority: homeUrl from auth → env var → "*" fallback.
const resolveParentOrigin = () => {
  try {
    const authDetails = JSON.parse(localStorage.getItem("authDetails"));
    if (authDetails?.homeUrl) {
      return new URL(authDetails.homeUrl).origin;
    }
  } catch {
    // ignore parse errors
  }
  return process.env.REACT_APP_PARENT_ORIGIN || "*";
};

const sendToParent = (eventType, payload = {}) => {
  if (!isInIframe()) return;

  const message = {
    source: BRIDGE_PREFIX,
    type: eventType,
    payload,
    timestamp: Date.now(),
  };

  try {
    const targetOrigin = resolveParentOrigin();
    window.parent.postMessage(message, targetOrigin);
  } catch {
    // silently swallow -- UI handles failure gracefully
  }
};

// --- Outgoing Events ---

export const emitCasinoReady = (userData) => {
  sendToParent("CASINO_READY", {
    uid: userData?.uid,
    operatorId: userData?.operatorId,
    balance: userData?.balance,
  });
};

export const emitBetPlaced = ({ marketId, amount, game, selectionName, type }) => {
  sendToParent("CASINO_BET_PLACED", {
    marketId,
    amount,
    game,
    selectionName,
    type,
  });
};

export const emitBetSettled = ({ marketId, won, amount, runnerName }) => {
  sendToParent("CASINO_BET_SETTLED", { marketId, won, amount, runnerName });
};

export const emitBalanceUpdated = (balance, exposure) => {
  sendToParent("CASINO_BALANCE_UPDATED", { balance, exposure });
};

export const emitGameOpened = (gameKey, gamePath) => {
  sendToParent("CASINO_GAME_OPENED", { gameKey, gamePath });
};

export const emitGameClosed = () => {
  sendToParent("CASINO_GAME_CLOSED", {});
};

export const emitSessionExpired = () => {
  sendToParent("CASINO_SESSION_EXPIRED", {});
};

export const emitCasinoExit = (homeUrl) => {
  sendToParent("CASINO_EXIT", { homeUrl });
};

export const emitCasinoError = (error) => {
  sendToParent("CASINO_ERROR", { message: error });
};

// --- Incoming Event Listener ---

let messageListenerAttached = false;
const incomingHandlers = {};

export const onParentMessage = (eventType, handler) => {
  if (!incomingHandlers[eventType]) {
    incomingHandlers[eventType] = [];
  }
  incomingHandlers[eventType].push(handler);
};

export const offParentMessage = (eventType, handler) => {
  if (!incomingHandlers[eventType]) return;
  incomingHandlers[eventType] = incomingHandlers[eventType].filter(
    (h) => h !== handler
  );
};

const handleIncomingMessage = (event) => {
  const { data } = event;
  if (!data || data.source !== "CASINO_IFRAME_PARENT") return;

  const handlers = incomingHandlers[data.type];
  if (handlers) {
    handlers.forEach((handler) => {
      try {
        handler(data.payload);
      } catch {
        // silently swallow -- handler errors are non-critical
      }
    });
  }
};

export const initBridge = () => {
  if (messageListenerAttached) return;
  window.addEventListener("message", handleIncomingMessage);
  messageListenerAttached = true;
};

export const destroyBridge = () => {
  window.removeEventListener("message", handleIncomingMessage);
  messageListenerAttached = false;
};
